import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NotificationService, Notification } from '../../utility/services/notification/notification.service';
import { Subject } from 'rxjs';
import { takeUntil, finalize, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationSyncService } from '../../utility/services/notification-sync/notification-sync.service';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss']
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('op') op!: any;
  unreadNotificationCount: number = 0;
  notifications: Notification[] = [];
  
  currentPage = 1;
  perPage = 10;
  hasMoreData = true;
  isLoading = false;
  
  selectedFilter = 'All';
  filters:string[]|[] = [];
  isPanelOpen = false;

  private destroy$ = new Subject<void>();
  private scrollThrottle = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private notificationSync: NotificationSyncService
  ) {}

  ngOnInit(): void {
    this.scrollThrottle.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => this.checkScrollAndLoad());

    // Listen for global "mark all as read" events from other components
    this.notificationSync.markAllRead$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications.forEach(n => n.is_read = true);
      });

    this.notificationSync.getUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadNotificationCount = count;
      });

      this.notificationSync.getNewNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe(newNotification => {
        if (newNotification) {
          this.notifications.unshift(newNotification);
          if (this.notifications.length > 30) {
            this.notifications.pop();
          }

        }
      });
    this.loadNotifications(true); 
  }

  togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;

    if (this.isPanelOpen) {
      this.loadNotifications(true);
    } else {
      this.selectedFilter = 'All';
    }
  }

  onPanelHide(): void {
    if (this.op) {
      this.op.hide();
    }
    this.isPanelOpen = false;
    // Reset filter when closed by clicking outside or other actions
    this.selectedFilter = 'All';
  }

  loadNotifications(reset = false): void {
    if (this.isLoading || (!this.hasMoreData && !reset)) return;

    if (reset) {
      this.currentPage = 1;
      this.notifications = [];
      this.hasMoreData = true;
    }

    this.isLoading = true;

    this.notificationService.getNotifications(this.currentPage, this.perPage, this.selectedFilter)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success === 1) {
            this.filters = response.filters || [];
            this.notificationSync.setUnreadCount(response.unreadCount); // Update the global unread count
            const newData = response.result || [];
            let combined = reset ? newData : [...this.notifications, ...newData];

            if (combined.length > 30) {
              combined = combined.slice(0, 30);
              this.hasMoreData = false; // Stop further API calls once 30 items are loaded
            } else {
              this.hasMoreData = this.currentPage < response.pagination.total_pages;
            }

            this.notifications = combined;
            this.hasMoreData = this.currentPage < response.pagination.total_pages;
            this.currentPage = response.pagination.current_page;
          }
        },
        error: (err) => console.error('API Error:', err)
      });
  }



  viewAllNotifications(): void {
    this.router.navigate(['/customer/account/notifications']);
    this.onPanelHide();
  }

  onContainerScroll(event: Event): void {
    this.scrollThrottle.next();
  }

  private checkScrollAndLoad(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;

    // Prevent loading more if we've already hit the 30 items cap
    if (this.notifications.length >= 30) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100 && this.hasMoreData && !this.isLoading) {
      this.currentPage++;
      this.loadNotifications();
    }
  }


  private parseDate(dateString: string): Date {
    if (!dateString) return new Date(NaN);

    let formatted = dateString.replace(' ', 'T');
    if (!formatted.endsWith('Z') && !formatted.includes('+') && !formatted.includes('-', 10)) {
      formatted += 'Z';
    }
    return new Date(formatted);
  }

  isToday(dateString: string): boolean {
    if (!dateString) return false;
    const date = this.parseDate(dateString);

    if (isNaN(date.getTime())) return false;

    const now = new Date();
    return now.toDateString() === date.toDateString();
  }

  markAsRead(notification: Notification): void {
    if (notification.is_read) return;

    this.notificationService.markAsRead([notification.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        notification.is_read = true;
        this.notificationSync.decrementUnreadCount();
      });
  }


  markAllAsRead(): void {
    const unreadIds = this.unreadNotificationCount;
    if (unreadIds == 0) return;

    this.notificationService.markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {

          this.notifications.forEach(n => {
            n.is_read = true;
          });

          this.notificationSync.clearUnreadCount();
          this.notificationSync.notifyAllMarkedAsRead();
        },
        error: (err) => {
          console.error(
            'Error marking all notifications as read:',
            err
          );
        }
      });
  }

  getIcon(type: string): string {
    const map: Record<string, string> = {
      'Orders': 'pi pi-box',
      'Payments': 'pi pi-wallet',
      'Ratings': 'pi pi-star',
      'System': 'pi pi-info-circle'
    };
    return map[type] || 'pi pi-bell';
  }


  getFilterIcon(filter: string): string {
    if (filter === 'All') {
      return 'pi pi-th-large'; // Default icon for the 'All' filter tab
    }

    const map: Record<string, string> = {
      'Orders': 'pi pi-box',
      'Payments': 'pi pi-wallet',
      'Ratings': 'pi pi-star',
      'System': 'pi pi-info-circle'
    };
    return map[filter] || 'pi pi-tag';
  }

  getStarsArray(rating: number | string): number[] {
    const num = Math.floor(Number(rating) || 0);
    return num > 0 ? Array(num).fill(0) : [];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = this.parseDate(dateString);

    if (isNaN(date.getTime())) return '';

    const now = new Date();

    // Check if it's the same calendar day in the user's local timezone
    const isToday = now.toDateString() === date.toDateString();
    if (isToday) return 'Today';

    // Check if it's yesterday
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = yesterday.toDateString() === date.toDateString();
    if (isYesterday) return 'Yesterday';

    // Calculate days ago for older items
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0 && diffDays < 7) {
      return `${diffDays}d ago`;
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  handleClick(notification: Notification): void {
    this.markAsRead(notification);
    if (notification.order_id) {
      this.router.navigate(['/customer/account/my-order-details'], { 
        queryParams: { order_id: notification.order_id } 
      });
    }

    // Explicitly close the PrimeNG OverlayPanel
    if (this.op) {
      this.op.hide();
    }

    this.isPanelOpen = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.scrollThrottle.complete();
  }
}